import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';

/**
 * Repère de visite guidée : une bulle ancrée sur un élément de l'écran désigné par son attribut
 * `data-tour`. Construit sur le `Popover` de Radix (positionnement, Échap, attributs d'accessibilité),
 * sans voile : l'écran reste utilisable pendant la visite. Les libellés viennent de l'appelant —
 * ce paquet ne traduit pas.
 *
 * Le composant est monté le temps de la visite, **une fois ses cibles affichées** : le premier
 * repère se cherche dans le DOM au montage. Un repère dont la cible a disparu entre-temps est
 * sauté ; au-delà du dernier, la visite se termine.
 */

export interface CoachMarkStep {
  /** Valeur de l'attribut `data-tour` de l'élément visé. */
  target: string;
  title: React.ReactNode;
  body: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export interface CoachMarkLabels {
  previous: string;
  next: string;
  finish: string;
  skip: string;
  /** « 2 / 5 », dans la langue de l'appelant. */
  counter: (current: number, total: number) => string;
}

export interface CoachMarkProps {
  steps: readonly CoachMarkStep[];
  labels: CoachMarkLabels;
  /** Dernier repère franchi. */
  onFinish: () => void;
  /** « Passer » ou Échap. */
  onSkip: () => void;
  /** Préfixe des identifiants de test, `coach-mark` par défaut. */
  testIdPrefix?: string;
}

function isDisplayed(element: Element): boolean {
  // `checkVisibility` écarte une cible masquée par `display: none` (variante mobile ou bureau) ;
  // absent du navigateur, on considère la cible visible.
  return typeof element.checkVisibility === 'function' ? element.checkVisibility() : true;
}

/**
 * Élément affiché portant `data-tour="target"`, ou `null`. Les cibles sont des clés écrites dans
 * le code (lettres, chiffres, tirets), jamais une saisie : le sélecteur n'a rien à échapper.
 */
export function findCoachMarkTarget(
  target: string,
  root: ParentNode = globalThis.document,
): Element | null {
  const candidates = root.querySelectorAll(`[data-tour="${target}"]`);
  for (const candidate of candidates) {
    if (isDisplayed(candidate)) return candidate;
  }
  return null;
}

/** Les repères dont la cible est présente, dans l'ordre : ce qu'une visite peut montrer maintenant. */
export function availableCoachMarkSteps<T extends Pick<CoachMarkStep, 'target'>>(
  steps: readonly T[],
  root: ParentNode = globalThis.document,
): T[] {
  return steps.filter((step) => findCoachMarkTarget(step.target, root) !== null);
}

interface Position {
  index: number;
  element: Element;
}

/** Premier repère présent à partir de `from`, dans le sens donné. */
function seek(
  steps: readonly CoachMarkStep[],
  from: number,
  direction: 1 | -1,
): Position | null {
  for (let index = from; index >= 0 && index < steps.length; index += direction) {
    const step = steps[index];
    const element = step === undefined ? null : findCoachMarkTarget(step.target);
    if (element !== null) return { index, element };
  }
  return null;
}

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function CoachMark({
  steps,
  labels,
  onFinish,
  onSkip,
  testIdPrefix = 'coach-mark',
}: CoachMarkProps) {
  const [position, setPosition] = React.useState<Position | null>(() => seek(steps, 0, 1));
  const primary = React.useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const bodyId = React.useId();

  const element = position?.element ?? null;
  const anchor = React.useMemo(() => (element === null ? null : { current: element }), [element]);

  React.useEffect(() => {
    element?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, [element]);

  if (position === null || anchor === null) return null;
  const step = steps[position.index];
  if (step === undefined) return null;

  const isFirst = position.index === 0;
  const isLast = position.index === steps.length - 1;

  const goNext = () => {
    const next = seek(steps, position.index + 1, 1);
    if (next === null) {
      onFinish();
      return;
    }
    setPosition(next);
  };

  const goPrevious = () => {
    const previous = seek(steps, position.index - 1, -1);
    if (previous !== null) setPosition(previous);
  };

  return (
    <PopoverPrimitive.Root open modal={false}>
      <PopoverPrimitive.Anchor virtualRef={anchor} />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={step.side ?? 'bottom'}
          align="center"
          sideOffset={10}
          collisionPadding={12}
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            primary.current?.focus();
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            onSkip();
          }}
          // Pas de voile : un clic ailleurs ne ferme pas la visite, il laisse simplement travailler.
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
          className={cn(
            'z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-popover-border bg-popover p-4 text-popover-foreground shadow-md outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 motion-reduce:animate-none',
          )}
          data-testid={testIdPrefix}
        >
          <div className="flex items-start justify-between gap-3">
            <p id={titleId} className="text-sm font-semibold">
              {step.title}
            </p>
            <span
              className="shrink-0 text-xs tabular-nums text-muted-foreground"
              data-testid={`text-${testIdPrefix}-counter`}
            >
              {labels.counter(position.index + 1, steps.length)}
            </span>
          </div>
          <p id={bodyId} className="mt-1.5 text-sm text-muted-foreground">
            {step.body}
          </p>
          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onSkip}
              data-testid={`button-${testIdPrefix}-skip`}
            >
              {labels.skip}
            </Button>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={goPrevious}
                  data-testid={`button-${testIdPrefix}-previous`}
                >
                  {labels.previous}
                </Button>
              )}
              <Button
                ref={primary}
                type="button"
                size="sm"
                onClick={goNext}
                data-testid={`button-${testIdPrefix}-${isLast ? 'finish' : 'next'}`}
              >
                {isLast ? labels.finish : labels.next}
              </Button>
            </div>
          </div>
          <PopoverPrimitive.Arrow className="fill-popover" width={12} height={6} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}